import AnimatedDiv from "components/AnimatedDiv";
import AnimatedDivHeader from "components/AnimatedDivHeader";
import AnimatedSection from "components/AnimatedSection";
import CustomAreaChart from "components/CustomAreaChart";
import CustomBarChart from "components/CustomBarChart";
import GlobeDestinos from "components/GlobeDestinos";
import CustomPieChart from "components/CustomPieChart";
import SegmentedBarChart from "components/SegmentedBarChart";
import ShowTotal from "components/ShowTotal";
import StatCard from "components/StatCard";
import TimeFilterGroup from "components/TimeFilterGroup";
import DashboardLayout from "layouts/DashboardLayout";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CotacaoPorData,
  CotadoXEntregue,
  defaultCotacaoPorData,
  getCotacoesHoje,
  getCotacoesMes,
  getCotacoesOntem,
  getCotacoesSemana,
  getCotadovsEntregue,
  getInfosBreves,
  getPaisDestinatario,
  getPaisValores,
  getQuantidadeStatus,
  getTransportadoraData,
  getValorMedioCotacoes,
  PaisDestinatario,
  PaisValor,
  TransportadoraData,
  TrendInfos,
} from "services/dashboard";

function Dashboard() {
  const { t } = useTranslation("dashboard");
  const [timeFilter, setTimeFilter] = useState(3);
  const [salesChartData, setSalesChartData] = useState<CotacaoPorData[]>(
    defaultCotacaoPorData,
  );
  const [countryValueData, setContryValueData] = useState<PaisValor[]>([]);
  const [barChart, setBarChart] = useState<CotadoXEntregue[]>([]);
  const [transportadoraData, setTransportadoraData] =
    useState<TransportadoraData[]>([]);
  const [destinosData, setDestinosData] = useState<PaisDestinatario[]>([]);
  const [trendInfos, setTrendInfos] = useState<TrendInfos[]>([]);
  const [valorMedio, setValorMedio] = useState("0.00");
  const [statusData, setStatusData] = useState([
    { label: "Criado", value: 0 },
    { label: "Entregue", value: 0 },
    { label: "Em transito", value: 0 },
    { label: "Cancelado", value: 0 },
  ]);

  useEffect(() => {
    getQuantidadeStatus().then((res) => {
      setStatusData(Array.isArray(res?.data) ? res.data : []);
    });
  }, []);

  useEffect(() => {
    switch (timeFilter) {
      case 0:
        getCotacoesHoje().then((res) => {
          setSalesChartData(res.data);
        });
        break;
      case 1:
        getCotacoesOntem().then((res) => {
          setSalesChartData(res.data);
        });
        break;
      case 2:
        getCotacoesSemana().then((res) => {
          setSalesChartData(res.data);
        });
        break;
      case 3:
        getCotacoesMes().then((res) => {
          setSalesChartData(res.data);
        });
        break;

      default:
        break;
    }
  }, [timeFilter]);

  useEffect(() => {
    getPaisValores().then((res) => {
      setContryValueData(Array.isArray(res?.data) ? res.data : []);
    });
  }, []);

  useEffect(() => {
    getCotadovsEntregue().then((res) => {
      setBarChart(Array.isArray(res?.data) ? res.data : []);
    });
  }, []);

  useEffect(() => {
    getTransportadoraData().then((res) => {
      setTransportadoraData(Array.isArray(res?.data) ? res.data : []);
    });
  }, []);

  useEffect(() => {
    getPaisDestinatario().then((res) => {
      setDestinosData(Array.isArray(res?.data) ? res.data : []);
    });
  }, []);

  useEffect(() => {
    getInfosBreves().then((res) => {
      setTrendInfos(Array.isArray(res?.data) ? res.data : []);
    });
  }, []);
  useEffect(() => {
    getValorMedioCotacoes().then((res) => {
      setValorMedio(String(res?.valorMedio ?? "0.00"));
    });
  }, []);
  const trendEntregues = trendInfos[0] ?? { value: 0, porcentagem: 0 };
  const trendCotacoes = trendInfos[1] ?? { value: 0, porcentagem: 0 };
  const trendTotal = trendInfos[2] ?? { value: 0, porcentagem: 0 };
  const trendTransito = trendInfos[3] ?? { value: 0, porcentagem: 0 };
  return (
    <>
      <DashboardLayout>
        <div className="flex flex-col gap-6 w-full ml-auto mr-auto">
          <AnimatedSection>
            {/* Topo da section */}
            <div className="flex items-center justify-between mb-6">
              <ShowTotal />

              <TimeFilterGroup
                filters={[
                  t("time_filters.today"),
                  t("time_filters.yesterday"),
                  t("time_filters.this_week"),
                  t("time_filters.this_month"),
                ]}
                activeFilter={timeFilter}
                onChange={setTimeFilter}
              />
            </div>

            {/* Grid de cards animados */}
            <div className="grid grid-cols-3 gap-6">
              <AnimatedDiv className="col-span-2">
                <AnimatedDivHeader
                  title={t("chart_titles.sales_performance")}
                />
                <CustomAreaChart data={salesChartData} />
              </AnimatedDiv>
              <AnimatedDiv entryFrom="left">
                <AnimatedDivHeader title={"Total de vendas por País"} />
                <SegmentedBarChart data={countryValueData} />
              </AnimatedDiv>
            </div>
          </AnimatedSection>
          <section className="grid grid-cols-3 gap-6">
            {transportadoraData.length > 0 && (
              <AnimatedDiv
                entryFrom="bottom"
                delay={0.2}
                className="col-span-1"
              >
                <AnimatedDivHeader title={t("chart_titles.by_carrier")} />
                <CustomPieChart
                  data={transportadoraData}
                  height={300}
                  colors={[
                    { key: "FEDEX", color: "#313F6B" },
                    { key: "UPS", color: "#98A2C0" },
                  ]}
                />
              </AnimatedDiv>
            )}
            {barChart.length > 0 && (
              <AnimatedDiv className="col-span-2">
                <AnimatedDivHeader
                  title={t("chart_titles.quotes_vs_shipments")}
                />
                <CustomBarChart
                  data={barChart}
                  xKey="label"
                  series={[
                    {
                      key: "cotacoes",
                      label: "Cotações",
                      color: "oklch(70.7% 0.165 254.624)",
                    },
                    {
                      key: "entregues",
                      label: "Entregues",
                      color: "oklch(42.4% 0.199 265.638",
                    },
                  ]}
                  height={300}
                />
              </AnimatedDiv>
            )}
          </section>
          <section className="grid grid-cols-3 gap-6">
            {destinosData.length > 0 && (
              <AnimatedDiv
                entryFrom="bottom"
                delay={0.2}
                className="col-span-2"
              >
                <AnimatedDivHeader title={"Destinos mais frequentes"} />
                {/* O endpoint devolve { label: "US", value: 12 } e o globo
                    espera { pais, total } — mesmos dados, nomes diferentes. */}
                <GlobeDestinos
                  data={destinosData.map((d) => ({
                    pais: d.label,
                    total: d.value,
                  }))}
                />
              </AnimatedDiv>
            )}
            <AnimatedDiv>
              <AnimatedDivHeader title={t("chart_titles.by_status")} />
              <CustomPieChart
                data={statusData}
                colors={[
                  { key: "Criado", color: "#9494a1" },
                  { key: "Entregue", color: "#32a852" },
                  { key: "Em transito", color: "#e8c70c" },
                ]}
              />
            </AnimatedDiv>
          </section>
          {trendInfos.length > 0 && (
            <section className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <StatCard
                title={"Entregues"}
                subtitle={"Hoje"}
                value={trendEntregues.value}
                trend={trendEntregues.porcentagem}
              />
              <StatCard
                title={"Cotações"}
                subtitle={"Hoje"}
                value={trendCotacoes.value}
                trend={trendCotacoes.porcentagem}
              />
              <StatCard
                title={"Cotações em transito"}
                subtitle={"Hoje"}
                value={trendTransito.value}
                trend={trendTransito.porcentagem}
              />
              <StatCard
                title={t("chart_titles.total_quotes")}
                subtitle={t("chart_titles.subtitles.total_quotes")}
                value={trendTotal.value}
                trend={trendTotal.porcentagem}
              />
              <StatCard
                title={"Valor médio por cotação"}
                subtitle={t("chart_titles.subtitles.total_quotes")}
                value={"USD$ " + valorMedio}
              />
            </section>
          )}
        </div>
      </DashboardLayout>
    </>
  );
}

export default Dashboard;
